<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const showBanner = ref(false)
const showSettings = ref(false)

const consent = ref({
  necessary: true, // Always true, cannot be changed
  functional: false,
  analytics: false,
  marketing: false,
})

const CONSENT_KEY = 'airpark_cookie_consent'

onMounted(() => {
  const savedConsent = localStorage.getItem(CONSENT_KEY)
  if (savedConsent) {
    consent.value = JSON.parse(savedConsent)
    updateGoogleConsent()
  } else {
    showBanner.value = true
  }
})

const acceptAll = () => {
  consent.value = {
    necessary: true,
    functional: true,
    analytics: true,
    marketing: true,
  }
  saveConsent()
}

const acceptNecessary = () => {
  consent.value = {
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false,
  }
  saveConsent()
}

const saveSettings = () => {
  saveConsent()
  showSettings.value = false
}

const saveConsent = () => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify(consent.value))
  showBanner.value = false
  updateGoogleConsent()
}

const updateGoogleConsent = () => {
  // Google Consent Mode V2
  if (typeof window === 'undefined') return

  const consentState = {
    'ad_storage': consent.value.marketing ? 'granted' : 'denied',
    'ad_user_data': consent.value.marketing ? 'granted' : 'denied',
    'ad_personalization': consent.value.marketing ? 'granted' : 'denied',
    'analytics_storage': consent.value.analytics ? 'granted' : 'denied',
    'functionality_storage': consent.value.functional ? 'granted' : 'denied',
    'personalization_storage': consent.value.functional ? 'granted' : 'denied',
  }

  // Försök skicka consent-uppdatering
  const sendConsent = () => {
    if ((window as any).gtag) {
      (window as any).gtag('consent', 'update', consentState)

      // Skicka även ett event för att bekräfta consent-uppdatering
      (window as any).gtag('event', 'consent_update', {
        'analytics_consent': consent.value.analytics ? 'granted' : 'denied',
        'marketing_consent': consent.value.marketing ? 'granted' : 'denied',
      })
      return true
    }
    return false
  }

  // Om gtag inte finns än, vänta och försök igen
  if (!sendConsent()) {
    setTimeout(sendConsent, 500)
  }
}

const openSettings = () => {
  showSettings.value = true
}
</script>

<template>
  <!-- Cookie Banner -->
  <Teleport to="body">
    <div v-if="showBanner && !showSettings" class="fixed bottom-0 left-0 right-0 z-50 p-4">
      <div class="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 p-6">
        <div class="flex flex-col md:flex-row md:items-center gap-4">
          <div class="flex-1">
            <h3 class="text-lg font-semibold text-gray-900 mb-2">{{ t('consent.title') }}</h3>
            <p class="text-gray-600 text-sm">
              {{ t('consent.description') }}
              <router-link to="/cookie-policy" class="text-primary-600 hover:underline">
                {{ t('consent.learnMore') }}
              </router-link>
            </p>
          </div>
          <div class="flex flex-col sm:flex-row gap-3">
            <button
              @click="openSettings"
              class="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              {{ t('consent.settings') }}
            </button>
            <button
              @click="acceptNecessary"
              class="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
            >
              {{ t('consent.necessary') }}
            </button>
            <button
              @click="acceptAll"
              class="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors text-sm font-medium"
            >
              {{ t('consent.acceptAll') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Settings Modal -->
    <div v-if="showSettings" class="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div class="absolute inset-0 bg-black/50" @click="showSettings = false"></div>
      <div class="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('consent.settingsTitle') }}</h2>
        
        <div class="space-y-4">
          <div class="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <h3 class="font-medium text-gray-900">{{ t('cookies.sections.types.necessary.title') }}</h3>
              <p class="text-sm text-gray-600">{{ t('cookies.sections.types.necessary.description') }}</p>
            </div>
            <span class="text-sm text-green-600 font-medium">{{ t('cookies.sections.types.necessary.status') }}</span>
          </div>
          
          <label class="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
            <div>
              <h3 class="font-medium text-gray-900">{{ t('cookies.sections.types.functional.title') }}</h3>
              <p class="text-sm text-gray-600">{{ t('cookies.sections.types.functional.description') }}</p>
            </div>
            <input type="checkbox" v-model="consent.functional" class="w-5 h-5 text-primary-600 rounded" />
          </label>
          
          <label class="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
            <div>
              <h3 class="font-medium text-gray-900">{{ t('cookies.sections.types.analytics.title') }}</h3>
              <p class="text-sm text-gray-600">{{ t('cookies.sections.types.analytics.description') }}</p>
            </div>
            <input type="checkbox" v-model="consent.analytics" class="w-5 h-5 text-primary-600 rounded" />
          </label>
          
          <label class="flex items-center justify-between p-4 bg-gray-50 rounded-lg cursor-pointer">
            <div>
              <h3 class="font-medium text-gray-900">{{ t('cookies.sections.types.marketing.title') }}</h3>
              <p class="text-sm text-gray-600">{{ t('cookies.sections.types.marketing.description') }}</p>
            </div>
            <input type="checkbox" v-model="consent.marketing" class="w-5 h-5 text-primary-600 rounded" />
          </label>
        </div>
        
        <div class="flex gap-3 mt-6">
          <button @click="showSettings = false" class="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
            {{ t('common.cancel') }}
          </button>
          <button @click="saveSettings" class="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg">
            {{ t('consent.save') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

